/*
 * Cart recommendations "Vous aimerez aussi" (snippets/cart-recommendations.liquid,
 * cart-recommendations-panel.liquid, sections/cart-recommendations.liquid)
 *
 * 1. Algolia Recommend "looking-similar" model, for the cart items (Algolia app
 *    config window.algoliaShopify.config, index of the market / language from
 *    getAlgoliaMarketLocaleConfig in assets/algolia.js).
 * 2. Items rendered by Shopify on the search route (ids in the Algolia order),
 *    with the cross-sell item of the cross-sell drawer.
 * The <cart-recommendations> element is re-created on every cart update: results
 * are cached per cart content to avoid refetching and flickering.
 * Adds use addCrossSellItem / updateCrossSellItemPrice (assets/cross-sell.js).
 */
const cartRecommendationsCache = new Map();

if (!customElements.get("cart-recommendations")) {
  customElements.define(
    "cart-recommendations",
    class CartRecommendations extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.list = this.querySelector("[data-cart-recommendations-list]");
        this.panel = document.getElementById(
          `CartRecommendationsPanel-${this.dataset.sectionId}`,
        );

        this.querySelector("[data-cart-recommendations-prev]")?.addEventListener(
          "click",
          () => this.slide(-1),
          { signal },
        );
        this.querySelector("[data-cart-recommendations-next]")?.addEventListener(
          "click",
          () => this.slide(1),
          { signal },
        );

        this.load();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      get cacheKey() {
        return [
          window.location.pathname,
          this.dataset.objectIds,
          this.dataset.exclude,
          this.dataset.limit,
        ].join("|");
      }

      async load() {
        if (!this.dataset.objectIds) {
          this.render("");
          return;
        }

        const key = this.cacheKey;
        if (!cartRecommendationsCache.has(key)) {
          cartRecommendationsCache.set(
            key,
            this.fetchItems().catch((error) => {
              console.error("[cart-recommendations]", error);
              return "";
            }),
          );
        }

        const html = await cartRecommendationsCache.get(key);
        if (this.isConnected) this.render(html);
      }

      // Product ids recommended by Algolia, merged across the cart items
      async fetchProductIds() {
        const config = window.algoliaShopify?.config || {};
        const appId = config.app_id || config.appId || config.application_id;
        const apiKey = config.search_api_key || config.searchApiKey || config.api_key;
        if (!appId || !apiKey) {
          console.warn("[cart-recommendations] Algolia app config not found (window.algoliaShopify.config)");
          return [];
        }

        const indexName =
          typeof getAlgoliaMarketLocaleConfig === "function"
            ? getAlgoliaMarketLocaleConfig().indexName
            : `${config.index_prefix || "shopify_"}products`;
        const limit = Number(this.dataset.limit) || 6;
        const objectIDs = [
          ...new Set(this.dataset.objectIds.split(",").filter(Boolean)),
        ];

        const response = await fetch(
          `https://${appId}-dsn.algolia.net/1/indexes/*/recommendations`,
          {
            method: "POST",
            headers: {
              "X-Algolia-Application-Id": appId,
              "X-Algolia-API-Key": apiKey,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              requests: objectIDs.map((objectID) => ({
                indexName,
                model: "looking-similar",
                objectID,
                maxRecommendations: limit * 2,
                threshold: 0,
              })),
            }),
          },
        );
        if (!response.ok) throw new Error(`Algolia Recommend: ${response.status}`);

        const { results = [] } = await response.json();
        const excluded = new Set(this.dataset.exclude.split(",").filter(Boolean));
        const hitLists = results.map((result) => result.hits || []);
        const ids = [];

        // Round-robin over the cart items so that each one is represented
        const longest = Math.max(0, ...hitLists.map((hits) => hits.length));
        for (let index = 0; index < longest && ids.length < limit; index++) {
          for (const hits of hitLists) {
            const id = String(hits[index]?.id ?? hits[index]?.product_id ?? "");
            if (!id || excluded.has(id) || ids.includes(id)) continue;
            ids.push(id);
            if (ids.length >= limit) break;
          }
        }
        return ids;
      }

      async fetchItems() {
        const ids = await this.fetchProductIds();
        if (!ids.length) return "";

        const root = theme.routes.root_url.replace(/\/$/, "");
        const query = ids.map((id) => `id:${id}`).join(" OR ");
        const response = await fetch(
          `${root}/search?type=product&options[unavailable_products]=hide&section_id=cart-recommendations&q=${encodeURIComponent(query)}`,
        );
        if (!response.ok) throw new Error(`Cart recommendations section: ${response.status}`);

        const content = new DOMParser()
          .parseFromString(await response.text(), "text/html")
          .querySelector("[data-cart-recommendations-content]");
        return content?.querySelector("[data-cross-sell-item]")
          ? content.innerHTML.trim()
          : "";
      }

      render(html) {
        this.list.innerHTML = html;
        this.hidden = !html;

        if (this.panel) {
          // Distinct ids for the variant pickers of the panel copy
          this.panel.querySelector("[data-cart-recommendations-list]").innerHTML =
            html.replace(/CrossSellVariant-/g, "CrossSellPanelVariant-");
          this.panel.hidden = !html;
        }
      }

      // Carousel (small screens): one item per slide
      slide(direction) {
        this.list.scrollBy({
          left: direction * this.list.clientWidth,
          behavior: "smooth",
        });
      }
    },
  );

  // Adds and variant pickers of the recommendations (mini cart and panel)
  document.addEventListener("submit", (event) => {
    const form = event.target.closest(
      ".cart-recommendations [data-cross-sell-item], .cart-recommendations-panel [data-cross-sell-item]",
    );
    if (!form) return;

    event.preventDefault();
    addCrossSellItem(
      form,
      document.getElementById("CrossSellDrawer")?.dataset.errorMessage,
    );
  });

  document.addEventListener("change", (event) => {
    const select = event.target.closest(
      ".cart-recommendations [data-cross-sell-variant], .cart-recommendations-panel [data-cross-sell-variant]",
    );
    if (select) updateCrossSellItemPrice(select);
  });
}
